/**
 * URL building for generic integration image widgets.
 *
 * A widget block stores `data-url-template` (the integration's
 * `image_url_template` with `{integration.*}` already resolved at drop) and
 * `data-config` (the `{widget.*}` values). The final `<img src>` is rebuilt by
 * substituting `{widget.<key>}` from the config.
 *
 * `{var=…}` Shopimind variables are left LITERAL — they are resolved per
 * recipient by the templating engine at send time, so they must never be
 * URL-encoded here (otherwise the engine wouldn't recognise them).
 */

/** Encode a config value for a URL query position, preserving `{var=…}` tokens. */
export const encodeIntegrationWidgetValue = (raw: unknown): string =>
  String(raw ?? '')
    .split(/(\{var=[^}]+\})/g)
    .map((part) => (part.startsWith('{var=') ? part : encodeURIComponent(part)))
    .join('');

/**
 * Like {@link encodeIntegrationWidgetValue}, but for the EDITOR preview: every
 * `{var=…}` token is replaced by `previewValue` (the integrator's example value)
 * so the image URL can actually evaluate. Used for display only — never for the
 * saved/sent URL (which keeps `{var=…}` literal).
 */
export const encodeIntegrationWidgetValuePreview = (
  raw: unknown,
  previewValue: string,
): string =>
  String(raw ?? '')
    .split(/(\{var=[^}]+\})/g)
    .map((part) =>
      part.startsWith('{var=')
        ? encodeURIComponent(previewValue)
        : encodeURIComponent(part),
    )
    .join('');

/**
 * Collect `{ fieldKey: preview_value }` from a widget `config_schema`
 * (fields / groups / style_groups). Feeds the editor image preview.
 */
export const extractIntegrationPreviewValues = (
  schema: any,
): Record<string, string> => {
  const out: Record<string, string> = {};
  if (!schema) return out;
  const collect = (fields: any[]) =>
    (fields || []).forEach((f) => {
      if (f?.key && f?.preview_value != null) {
        out[f.key] = String(f.preview_value);
      }
    });
  collect(schema.fields);
  (schema.groups || []).forEach((g: any) => collect(g?.fields));
  (schema.style_groups || []).forEach((g: any) => collect(g?.fields));
  return out;
};

/**
 * Replace every `{widget.<key>}` placeholder in `urlTemplate` with the
 * (encoded) value from `config`. Unknown keys resolve to an empty string.
 * `{integration.*}` (already resolved at drop) and `{var=*}` are left as-is —
 * UNLESS a `previewValues[key]` is provided AND the value holds a `{var=…}`:
 * then the variable is swapped for the example value (editor image preview only;
 * the persisted URL keeps `{var=…}`).
 */
export const substituteIntegrationWidgetUrl = (
  urlTemplate: string,
  config: Record<string, unknown>,
  previewValues?: Record<string, string>,
): string =>
  (urlTemplate || '').replace(/\{widget\.([a-zA-Z0-9_]+)\}/g, (_match, key) => {
    const raw = config?.[key];
    const preview = previewValues?.[key];
    if (preview != null && /\{var=/.test(String(raw ?? ''))) {
      return encodeIntegrationWidgetValuePreview(raw, preview);
    }
    return encodeIntegrationWidgetValue(raw);
  });
